import { endOfDayExclusive, minutesOfDayInZone, startOfDayInZone, todayInZone, weekdayOfYmd, ymdOfInstant } from "./zone.js";

/**
 * Pure computation for the staff-availability grid (StaffAvailabilityGrid.tsx). Everything here is in the BUSINESS's local time (lib/zone.ts:
 * its state's Australian time zone), never the browser's: the weekday, the minutes of the day, "today" and "now" are the business's, so a
 * phone set to another zone, or an admin looking at a business in another state, sees the same grid the business does.
 */

export interface AvailabilityWindowRow {
  resource_id: string;
  weekday: number; // 0 = Sunday
  start_time: string; // "HH:MM:SS"
  end_time: string;
}

export interface AvailabilityExceptionRow {
  resource_id: string | null; // null = whole business
  starts_at: string; // ISO
  ends_at: string;
  kind: "leave" | "holiday" | "blockout" | "extra_hours";
}

export interface BusyItem {
  resource_id: string;
  starts_at: string;
  ends_at: string;
  kind: "booking" | "hold";
  label: string;
  /** For a booking: which booking this block belongs to (makes the block clickable in the grid). */
  bookingId?: string;
}

export type SegmentKind = "closed" | "open" | "busy" | "hold";

export interface DaySegment {
  startMin: number;
  endMin: number;
  kind: SegmentKind;
  label?: string;
  bookingId?: string;
}

const DEFAULT_RANGE = { start: 8 * 60, end: 19 * 60 }; // 8:00-19:00 fallback when nothing configured

export function weekdayOf(dateStr: string): number {
  return weekdayOfYmd(dateStr);
}

/** Today's date at the business. */
export function todayStr(): string {
  return todayInZone();
}

/** Minutes-of-day that should be treated as already past for `dateStr`: null for any future
 *  date (nothing to cut), 1440 (the whole day) for any date before today, or "now" for today
 *  itself. Used to stop a new booking or reschedule from ever landing in the past. */
function pastCutoffFor(dateStr: string): number | null {
  const today = todayStr();
  if (dateStr < today) return 1440;
  if (dateStr > today) return null;
  return minutesOfDayInZone(Date.now());
}

export function timeStrToMin(t: string): number {
  const parts = t.split(":").map(Number);
  return (parts[0] ?? 0) * 60 + (parts[1] ?? 0);
}

export function minToTimeLabel(min: number): string {
  const h24 = Math.floor(min / 60) % 24;
  const m = min % 60;
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  const suffix = h24 < 12 ? "am" : "pm";
  return m === 0 ? `${h12}${suffix}` : `${h12}:${String(m).padStart(2, "0")}${suffix}`;
}

function localMinutesOfDay(iso: string): number {
  return minutesOfDayInZone(iso);
}

function isSameLocalDate(iso: string, dateStr: string): boolean {
  return ymdOfInstant(iso) === dateStr;
}

/** Clamps a busy/exception interval that may start before or end after the displayed day to
 *  the day's own [0, 1440) range, so a booking spanning midnight doesn't render nonsense. */
function clampToDay(startIso: string, endIso: string, dateStr: string): [number, number] | null {
  const startsToday = isSameLocalDate(startIso, dateStr);
  const endsToday = isSameLocalDate(endIso, dateStr);
  if (!startsToday && !endsToday) {
    // Spans the whole day only if it genuinely covers it (rare; treat as all-day busy).
    const s = new Date(startIso);
    const e = new Date(endIso);
    const dayStart = startOfDayInZone(dateStr);
    const dayEnd = new Date(endOfDayExclusive(dateStr).getTime() - 1000); // the last second of the day
    if (s <= dayStart && e >= dayEnd) return [0, 1440];
    return null;
  }
  const startMin = startsToday ? localMinutesOfDay(startIso) : 0;
  const endMin = endsToday ? localMinutesOfDay(endIso) : 1440;
  return [Math.max(0, startMin), Math.min(1440, endMin)];
}

/** For each resource, merges its weekly availability window(s) for the given date's weekday,
 *  minus leave/holiday/blockout exceptions (plus extra_hours as additional open time), then
 *  overlays busy (confirmed booking) and hold (live TTL hold) segments on top. Returns segments
 *  already sorted and clamped to the visible day, plus the overall min/max open time across all
 *  resources (falling back to a sensible default range if nothing is configured yet, so the
 *  grid still renders something useful instead of collapsing to nothing). */
export function buildDaySchedule(
  resourceIds: string[],
  windows: AvailabilityWindowRow[],
  exceptions: AvailabilityExceptionRow[],
  busy: BusyItem[],
  dateStr: string
): { byResource: Map<string, DaySegment[]>; rangeStart: number; rangeEnd: number } {
  const weekday = weekdayOf(dateStr);
  const byResource = new Map<string, DaySegment[]>();
  let rangeStart = Infinity;
  let rangeEnd = -Infinity;

  for (const resourceId of resourceIds) {
    let open: Array<[number, number]> = windows
      .filter((w) => w.resource_id === resourceId && w.weekday === weekday)
      .map((w) => [timeStrToMin(w.start_time), timeStrToMin(w.end_time)] as [number, number]);

    const extraHours = exceptions.filter(
      (ex) => (ex.resource_id === resourceId || ex.resource_id === null) && ex.kind === "extra_hours" && overlapsDate(ex, dateStr)
    );
    for (const ex of extraHours) {
      const clamped = clampToDay(ex.starts_at, ex.ends_at, dateStr);
      if (clamped) open.push(clamped);
    }

    const blocked = exceptions.filter(
      (ex) =>
        (ex.resource_id === resourceId || ex.resource_id === null) &&
        (ex.kind === "leave" || ex.kind === "holiday" || ex.kind === "blockout") &&
        overlapsDate(ex, dateStr)
    );

    open = mergeIntervals(open);
    for (const b of blocked) {
      const clamped = clampToDay(b.starts_at, b.ends_at, dateStr);
      if (clamped) open = subtractInterval(open, clamped);
    }

    const cutoff = pastCutoffFor(dateStr);
    if (cutoff != null && cutoff > 0) open = subtractInterval(open, [0, cutoff]);

    const segments: DaySegment[] = open.map(([s, e]) => ({ startMin: s, endMin: e, kind: "open" as const }));

    for (const item of busy.filter((b) => b.resource_id === resourceId)) {
      const clamped = clampToDay(item.starts_at, item.ends_at, dateStr);
      if (!clamped) continue;
      insertBusy(segments, clamped[0], clamped[1], item.kind === "hold" ? "hold" : "busy", item.label, item.bookingId);
    }

    segments.sort((a, b) => a.startMin - b.startMin);
    byResource.set(resourceId, segments);

    for (const [s, e] of open) {
      if (s < rangeStart) rangeStart = s;
      if (e > rangeEnd) rangeEnd = e;
    }
  }

  if (!Number.isFinite(rangeStart) || !Number.isFinite(rangeEnd) || rangeStart >= rangeEnd) {
    rangeStart = DEFAULT_RANGE.start;
    rangeEnd = DEFAULT_RANGE.end;
  }
  return { byResource, rangeStart, rangeEnd };
}

function overlapsDate(ex: { starts_at: string; ends_at: string }, dateStr: string): boolean {
  const dayStart = startOfDayInZone(dateStr);
  const dayEnd = new Date(endOfDayExclusive(dateStr).getTime() - 1);
  return new Date(ex.starts_at) <= dayEnd && new Date(ex.ends_at) >= dayStart;
}

function mergeIntervals(intervals: Array<[number, number]>): Array<[number, number]> {
  const sorted = [...intervals].sort((a, b) => a[0] - b[0]);
  const merged: Array<[number, number]> = [];
  for (const [s, e] of sorted) {
    const last = merged[merged.length - 1];
    if (last && s <= last[1]) last[1] = Math.max(last[1], e);
    else merged.push([s, e]);
  }
  return merged;
}

function subtractInterval(intervals: Array<[number, number]>, [bs, be]: [number, number]): Array<[number, number]> {
  const result: Array<[number, number]> = [];
  for (const [s, e] of intervals) {
    if (be <= s || bs >= e) {
      result.push([s, e]);
      continue;
    }
    if (bs > s) result.push([s, bs]);
    if (be < e) result.push([be, e]);
  }
  return result;
}

/** Splits whatever "open" segment currently covers [start,end) to insert a busy/hold block,
 *  leaving the open remainder(s) either side. Segments outside any open block are dropped
 *  (a busy item can only ever be inside a real booking_items row, which the exclusion
 *  constraint already guarantees doesn't fall outside real availability in practice). */
function insertBusy(segments: DaySegment[], start: number, end: number, kind: "busy" | "hold", label: string, bookingId?: string): void {
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    if (!seg || seg.kind !== "open" || end <= seg.startMin || start >= seg.endMin) continue;
    const overlapStart = Math.max(start, seg.startMin);
    const overlapEnd = Math.min(end, seg.endMin);
    const replacement: DaySegment[] = [];
    if (seg.startMin < overlapStart) replacement.push({ startMin: seg.startMin, endMin: overlapStart, kind: "open" });
    replacement.push({ startMin: overlapStart, endMin: overlapEnd, kind, label, bookingId });
    if (overlapEnd < seg.endMin) replacement.push({ startMin: overlapEnd, endMin: seg.endMin, kind: "open" });
    segments.splice(i, 1, ...replacement);
    i += replacement.length - 1;
  }
}

/** Whether [startMin, startMin+durationMin) is fully open (no busy/hold/closed) for a
 *  resource's computed day segments -- used to validate a click-to-book slot client-side.
 *  The database's exclusion constraint remains the real source of truth on submit. */
export function isSlotOpen(segments: DaySegment[], startMin: number, durationMin: number): boolean {
  const endMin = startMin + durationMin;
  return segments.some((s) => s.kind === "open" && s.startMin <= startMin && s.endMin >= endMin);
}
